import { useEffect, useRef, useState } from "react";
import { Image, ImagePlus, Pencil, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import {
  LOGIN_SPLASH_IMAGES,
  normaliseLoginCopy,
  splashImagesWithUploads,
} from "@/lib/loginScreen";

const COPY_FIELDS = [
  ["brand_name", "Product name"],
  ["school_sign_in", "Sign-in caption"],
  ["hero_eyebrow", "Splash eyebrow"],
  ["hero_title", "Splash heading", true],
  ["hero_body", "Splash description", true],
  ["manual_reassurance", "Manual-work message", true],
  ["email_label", "Email label"],
  ["password_label", "Password label"],
  ["sign_in_label", "Sign-in button"],
  ["signing_in_label", "Signing-in button"],
  ["divider_label", "Sign-in divider"],
  ["google_label", "Google sign-in button"],
  ["google_help", "Google sign-in note", true],
  ["forgot_password_label", "Forgot-password link"],
  ["register_school_label", "Register-school link"],
  ["signup_prefix", "Register-school prefix"],
  ["signup_link_label", "Free-trial link"],
  ["faq_label", "FAQ link"],
];

export function SuperLoginScreenPanel() {
  const [screen, setScreen] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);

  useEffect(() => {
    api
      .get("/public/login-screen")
      .then(({ data }) => {
        const content = normaliseLoginCopy(data);
        setScreen(content);
        setDraft(content);
      })
      .catch((error) => toast.error(errMsg(error)));
  }, []);

  const toggleImage = (imageId) => {
    setDraft((current) => {
      if (current.splash_mode === "static") {
        return { ...current, splash_image_ids: [imageId] };
      }
      const selected = current.splash_image_ids.includes(imageId)
        ? current.splash_image_ids.filter((id) => id !== imageId)
        : [...current.splash_image_ids, imageId];
      return { ...current, splash_image_ids: selected };
    });
  };
  const setSplashMode = (splashMode) => {
    setDraft((current) => ({
      ...current,
      splash_mode: splashMode,
      splash_image_ids: splashMode === "static"
        ? [current.splash_image_ids[0] || LOGIN_SPLASH_IMAGES[0].id]
        : current.splash_image_ids,
    }));
  };
  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/super/login-splash-images", formData);
      setScreen((current) => ({
        ...current,
        custom_images: [...current.custom_images, data],
      }));
      setDraft((current) => ({
        ...current,
        custom_images: [...current.custom_images, data],
        splash_image_ids: current.splash_mode === "static"
          ? [data.id]
          : [...current.splash_image_ids, data.id],
      }));
      toast.success("Splash image uploaded and selected");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading(false);
    }
  };
  const deleteImage = async (image) => {
    if (!window.confirm(`Delete ${image.label} from the splash image library?`)) return;
    try {
      await api.delete(`/super/login-splash-images/${image.id}`);
      const removeImage = (current) => {
        const selected = current.splash_image_ids.filter((id) => id !== image.id);
        return {
          ...current,
          custom_images: current.custom_images.filter((item) => item.id !== image.id),
          splash_image_ids: selected.length ? selected : [LOGIN_SPLASH_IMAGES[0].id],
        };
      };
      setScreen(removeImage);
      setDraft(removeImage);
      toast.success("Splash image deleted");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/super/login-screen", draft);
      const content = normaliseLoginCopy(data);
      setScreen(content);
      setDraft(content);
      setEditing(false);
      toast.success("Login screen published");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  const splashImages = splashImagesWithUploads(draft?.custom_images);

  return (
    <section
      className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b pb-8"
      data-testid="super-login-screen-panel"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          Service settings
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold">Login screen</h2>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="super-login-screen-status">
          {screen
            ? screen.splash_mode === "static"
              ? "One fixed splash image selected"
              : `${screen.splash_image_ids.length} splash images selected at random`
            : "Loading login screen…"}
        </p>
      </div>
      <Button
        size="sm"
        disabled={!screen}
        onClick={() => {
          setDraft(screen);
          setEditing(true);
        }}
        data-testid="super-login-screen-edit-button"
      >
        <Pencil className="mr-2 h-4 w-4" />
        Edit login screen
      </Button>
      <Dialog open={editing} onOpenChange={(open) => !open && setEditing(false)}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="super-login-screen-dialog">
          <DialogHeader>
            <DialogTitle>Edit login screen</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-6 py-2">
              <div className="grid gap-4 sm:grid-cols-2">
                {COPY_FIELDS.map(([key, label, multiline]) => (
                  <div key={key} className={multiline ? "space-y-1.5 sm:col-span-2" : "space-y-1.5"}>
                    <Label htmlFor={`login-screen-${key}`}>{label}</Label>
                    {multiline ? (
                      <Textarea
                        id={`login-screen-${key}`}
                        value={draft[key]}
                        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                        data-testid={`login-screen-field-${key}`}
                      />
                    ) : (
                      <Input
                        id={`login-screen-${key}`}
                        value={draft[key]}
                        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                        data-testid={`login-screen-field-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-primary" />
                  <Label>Splash images</Label>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Splash display mode">
                  <Button
                    type="button"
                    size="sm"
                    variant={draft.splash_mode === "random" ? "default" : "outline"}
                    className={draft.splash_mode === "random" ? "" : "text-foreground"}
                    onClick={() => setSplashMode("random")}
                    aria-pressed={draft.splash_mode === "random"}
                    data-testid="login-splash-mode-random"
                  >
                    Random selected image
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={draft.splash_mode === "static" ? "default" : "outline"}
                    className={draft.splash_mode === "static" ? "" : "text-foreground"}
                    onClick={() => setSplashMode("static")}
                    aria-pressed={draft.splash_mode === "static"}
                    data-testid="login-splash-mode-static"
                  >
                    One fixed image
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {draft.splash_mode === "random"
                    ? "Choose one or more images. One appears at random on each visit."
                    : "Choose the one image that appears on every visit."}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs font-medium text-muted-foreground">Built-in images</p>
                  <input
                    ref={uploadRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={uploadImage}
                    data-testid="login-splash-upload-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => uploadRef.current?.click()}
                    data-testid="login-splash-upload-button"
                  >
                    <ImagePlus className="mr-2 h-4 w-4" />
                    {uploading ? "Uploading…" : "Upload your image"}
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {splashImages.filter((image) => !image.custom).map((image) => {
                    const selected = draft.splash_image_ids.includes(image.id);
                    return (
                      <button
                        key={image.id}
                        type="button"
                        className={`overflow-hidden rounded-lg border text-left transition-colors ${selected ? "border-primary ring-2 ring-primary/25" : "hover:border-primary/50"}`}
                        onClick={() => toggleImage(image.id)}
                        aria-pressed={selected}
                        data-testid={`login-splash-image-${image.id}`}
                      >
                        <img
                          src={image.url}
                          alt=""
                          className="aspect-[16/9] w-full object-cover"
                        />
                        <span className="block px-2.5 py-2 text-xs font-medium">{image.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-5">
                  <p className="text-xs font-medium text-muted-foreground">Your uploaded images</p>
                  {draft.custom_images.length === 0 ? (
                    <p className="mt-2 text-xs text-muted-foreground" data-testid="login-splash-upload-empty">
                      Upload a JPEG, PNG or WebP image up to 5 MB to add it here.
                    </p>
                  ) : (
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {splashImages.filter((image) => image.custom).map((image) => {
                        const selected = draft.splash_image_ids.includes(image.id);
                        return (
                          <div key={image.id} className="relative overflow-hidden rounded-lg border">
                            <button
                              type="button"
                              className={`w-full text-left transition-colors ${selected ? "ring-2 ring-primary/25" : "hover:bg-muted/40"}`}
                              onClick={() => toggleImage(image.id)}
                              aria-pressed={selected}
                              data-testid={`login-splash-image-${image.id}`}
                            >
                              <img
                                src={image.url}
                                alt=""
                                className="aspect-[16/9] w-full object-cover"
                              />
                              <span className="block truncate px-2.5 py-2 pr-9 text-xs font-medium">
                                {image.label}
                              </span>
                            </button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute bottom-1 right-1 h-7 w-7 text-destructive"
                              title={`Delete ${image.label}`}
                              onClick={() => deleteImage(image)}
                              data-testid={`login-splash-delete-${image.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setEditing(false)}
              data-testid="super-login-screen-cancel-button"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              disabled={saving || !draft?.splash_image_ids.length}
              onClick={save}
              data-testid="super-login-screen-save-button"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Publishing…" : "Publish login screen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}